import { useCallback, useMemo } from 'react';
import { MultiLineChartDataset, useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { api } from '@/api';
import { RAW_DATE_FORMAT, SHORT_MONTH_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';
import theme from '@/styles/theme';

const useGetBalanceSpendQuery = (planId: string) => {
  const { isLoading, data, isError, refetch, isSuccess } = useQuery(
    [planId],
    () => api.get(API_URL.ACCOUNT_USAGE_PLANS_BALANCES_HSA_BY_PLAN_ID(planId)),
    {
      retry: 0,
    }
  );

  const totalValues = useMemo(() => {
    const currentData = data?.data;

    return {
      cashBalance: currentData?.current_cash_balance || 0,
      investmentsBalance: currentData?.current_investment_balance || 0,
    };
  }, [data?.data]);
  const dates = useMemo<Date[]>(() => {
    const currentData = data?.data?.monthly_breakdown || [];
    return currentData?.map((item) => dayjs(item.date, RAW_DATE_FORMAT).toDate()) || [];
  }, [data?.data]);
  const labels = useMemo<string[]>(() => {
    const currentData = data?.data?.monthly_breakdown || [];
    return currentData?.map((item) => dayjs(item.date, RAW_DATE_FORMAT).format(SHORT_MONTH_FORMAT)) || [];
  }, [data?.data]);
  const currentDateIndex = useMemo(() => {
    const index = dates.findIndex((item) => dayjs(item).isSame(dayjs(), 'month'));
    return index;
  }, [dates]);
  const datasets = useMemo<MultiLineChartDataset[]>(() => {
    const currentData = data?.data?.monthly_breakdown || [];

    const result: MultiLineChartDataset[] = [];
    const lastIndex = currentDateIndex >= 0 ? currentDateIndex + 1 : currentData?.length || 1;
    if (totalValues.cashBalance) {
      const item: MultiLineChartDataset = {
        label: 'Cash Balance',
        values:
          currentData && currentData?.length
            ? currentData?.map((item) => item.cash_balance).splice(0, lastIndex)
            : [],
        color: theme.colors.iconAccent,
      };
      result.push(item);
    }
    if (totalValues.investmentsBalance) {
      const item: MultiLineChartDataset = {
        label: 'Investments balance',
        values:
          currentData && currentData?.length
            ? currentData?.map((item) => item.investment_balance).splice(0, lastIndex)
            : [],
        color: theme.colors.needsHelp,
      };
      result.push(item);
    }
    return result;
  }, [
    currentDateIndex,
    data?.data?.monthly_breakdown,
    totalValues.cashBalance,
    totalValues.investmentsBalance,
  ]);
  const delimiters = useMemo(() => {
    const result: { index: number; label: string }[] = [];
    (dates || []).forEach((date, index) => {
      if (index === 0) {
        return;
      }
      const currentYear = dayjs(date).year();
      const previousYear = dayjs(dates[index - 1]).year();
      if (currentYear !== previousYear) {
        result.push({ index, label: `${currentYear}` });
      }
    });
    return result;
  }, [dates]);
  const handleRefetch = useCallback(() => {
    if (!isLoading) {
      refetch();
    }
  }, [isLoading, refetch]);
  return {
    isSuccess,
    refetch: handleRefetch,
    dates,
    datasets,
    delimiters,
    labels,
    totalValues,
    currentDateIndex,
    data,
    isLoading,
    isError,
  };
};

export default useGetBalanceSpendQuery;
